<template>
  <div ref="editorWrapper" :class="['editor', ...themeClasses]">
    <!-- 热词 -->
    <div v-show="!showHintList" class="keyword-bar">
      <span class="title">我想问</span>
      <div class="keyword-sroll">
        <div
          v-for="hotWord in hotWordList"
          :key="hotWord.word"
          class="capsule"
          @click="onClickHotWord(hotWord)"
        >
          {{ hotWord.word }}
        </div>
      </div>
    </div>
    <!-- 输入栏 -->
    <div
      ref="inputBar"
      class="input-bar"
      :class="{ inputing: inputText }"
      :style="textBarStyle"
    >
      <div class="picker-item">
        <div class="img-box" @click="onOpenAlblum()">
          <img src="@/assets/img/feedback/image_icon.svg" />
        </div>
      </div>
      <!-- 文本输入框 -->
      <textarea
        ref="inputBox"
        v-model="inputText"
        rows="1"
        :style="textareaStyle"
        placeholder="在此输入您宝贵的意见"
        @keydown="autoResize"
        @focus="onFocus"
        @blur="onBlur"
      />
      <van-button :disabled="!inputText" class="send-btn" @click="onSend">
        {{ inputText ? '完成' : '发送' }}
      </van-button>
      <!-- 关联问题提示 -->
      <div
        v-show="showHintList"
        ref="hintView"
        class="hint-list"
        :style="hintViewStyle"
      >
        <div
          v-for="hint in hintList"
          :key="getHintKey(hint)"
          class="hint-item"
          @click="onClickHint(hint)"
        >
          <p>
            <!-- span必须这么写，要不然文本之间会有多余的空格 -->
            <span
              v-for="(hItem, hIndex) in hint"
              :key="hIndex"
              :class="{ 'hight-light': hItem.highlight }"
              >{{ hItem.word }}</span
            >
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { rem2px } from '@/utils/formatter'
import { Button as VanButton, showLoadingToast, closeToast } from 'vant'
import { compareVersion } from '@/utils/tools'
import { m2uBridge } from '@/utils/m2u-bridge'
import { messageOriginMap } from '@/utils/constant'

export default {
  name: 'Editor',
  components: { VanButton },
  props: {
    hotWordList: {
      type: Array,
      default: () => [],
    },
    post: {
      type: Function,
      default: () => {},
    },
    userId: {
      type: String,
      default: '',
    },
    businessId: {
      type: Number,
      default: 0,
    },
    /** 主题定制，支持传数组字符串，方便不同的基本主题搭配使用，比如白色背景搭配粉色按钮 */
    theme: {
      type: [String, Array],
      default: 'dark',
    },
    /** 布局方式
     *
     * - `collapse` 折叠式
     * - `horizontal` 水平排列
     */
    layout: {
      type: String,
      default: 'collapse',
    },
  },
  data() {
    return {
      inputText: '',
      isFocused: false,
      hintItemH: 0,
      debounceHintList: null,
      hintList: [],
      textareaStyle: {},
      showPickerView: false,
      isBelow612: compareVersion('6.12.0'),
    }
  },
  computed: {
    textBarStyle() {
      if (this.showPickerView || this.isFocused) {
        return {
          'padding-bottom': '0.32rem',
        }
      }
      return {
        'padding-bottom': '0.32rem', // 容器不需要键盘顶起padding，暂时设置为一样
      }
    },
    showHintList() {
      return this.hintList.length && this.isFocused
    },
    hintViewStyle() {
      const h = this.hintList.length * this.hintItemH
      return { top: `-${h}px` }
    },
    themeClasses() {
      const themes = Array.isArray(this.theme) ? this.theme : [this.theme]
      return themes.map((th) => `theme-${th}`)
    },
  },
  watch: {
    inputText(val) {
      this.autoResize()
      this.debounceHintList()
      localStorage.setItem('feedback_m2u_input_text', val)
    },
    showPickerView() {
      this.$nextTick(() => {
        this.$emit('change-height', this.getNormalEditorHeight())
      })
    },
    showHintList() {
      this.$nextTick(() => {
        this.$emit('change-height', this.getNormalEditorHeight())
      })
    },
  },
  mounted() {
    // 使用绑定注册方便使用api注释

    // m2uBridge.init(this.$yoda)
    this.hintItemH = rem2px(0.82)

    this.inputText = localStorage.getItem('feedback_m2u_input_text') || ''
    this.$emit('change-height', this.getNormalEditorHeight())

    this.debounceHintList = debounce(this.getInputHintList, 300)

    this.onOpenAlblum = debounce(this.onOpenAlblum, 500)
  },
  beforeDestroy() {
    m2uBridge.destroy()
  },
  methods: {
    onOpenAlblum() {
      this.$nativeLog.collect('CLICK', 'YT_UPLOAD_PHOTO_VIDEO')
      m2uBridge
        .selectFileAndUpload({
          onProgress: (data) => {
            console.log('===data', data)
            const progress =
              data?.progress !== undefined
                ? ` ${(data.progress * 100).toFixed(0)}%`
                : ''
            showLoadingToast({
              message: `上传中...\n${progress}`,
              duration: 0,
              loadingType: 'spinner',
            })
          },
        })
        .finally(() => closeToast())
        .then((files) => {
          const infos = files
            .map((file) => {
              if (file.result !== 1) return
              const type = file?.type
              const isImg = type?.startsWith('Image')
              const logParams = {
                businessId: this.businessId,
                type: isImg ? 'image' : 'video',
                fileType: type,
              }
              this.$nativeLog.collect(
                'CLICK',
                'yt_feedback_choose_upload_file',
                logParams
              )
              const { width, height, token } = file
              return {
                type: isImg ? 2 : 3,
                content: token,
                extra: JSON.stringify({
                  w: width || 0,
                  h: height || 0,
                }),
              }
            })
            .filter(Boolean)
          this.$emit('send-file', infos)
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e)
        })
    },
    onClickHotWord(hotWord) {
      this.$emit('send-hot-word', hotWord)
    },
    onClickHint(hint) {
      this.$emit('send', {
        word: hint.map((item) => item.word).join(''),
      })
    },
    onSend() {
      this.$nativeLog.collect('CLICK', 'yt_feedback_send_click', {
        userId: this.userId,
        upload_method: 'native',
      })
      this.$emit('send', {
        word: this.inputText,
        messageOrigin: messageOriginMap.User,
      })
    },
    blur() {
      this.$refs.inputBox.blur()
    },
    onFocus() {
      this.$emit('input-focus')
      this.isFocused = true
      this.showPickerView = false
    },
    onBlur() {
      this.$emit('input-blur')
      // 点击hint-list时延时隐藏
      setTimeout(() => {
        this.isFocused = false
      }, 0)
    },
    /** 孤岛属性，不要删，外面使用了ref引用 */
    clearInput() {
      this.inputText = ''
    },
    getNormalEditorHeight() {
      return (
        this.$refs.editorWrapper.clientHeight + this.$refs.hintView.clientHeight
      )
    },
    async getInputHintList() {
      try {
        const res = await this.post('/rest/n/fb/app/dialog/getInputHint', {
          content: this.inputText,
        })

        if (res.result === 1) {
          this.hintList = res.highlightList
        }
      } catch (e) {}
    },
    autoResize() {
      this.textareaStyle = {
        height: 'auto',
      }
      this.$nextTick(() => {
        let h = this.$refs.inputBox.scrollHeight
        const maxH = rem2px(2.3)
        let scrollable = false
        if (h > maxH) {
          h = maxH
          scrollable = true
        }
        this.textareaStyle = {
          height: `${h}px`,
          'overflow-y': scrollable ? 'scroll' : 'hidden',
        }
        this.$nextTick(() => {
          this.$emit('change-height', this.getNormalEditorHeight())
        })
      })
    },
    getHintKey(hint) {
      return hint.reduce((prevResult, curItem) => prevResult + curItem.word, '')
    },
  },
}
</script>
<style lang="less" scoped>
@font-family-default: PingFangSC-Regular;
@font-family-medium: PingFangSC-Medium;

.editor {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: #141414;
  border-top: 0.01rem solid rgba(255, 255, 255, 0.06);
  color: rgba(25, 25, 25, 0.7);
  font-size: 0.26rem;
  z-index: 999;

  // 主题色定制
  .theme-white& {
    border: none;
    color: rgba(103, 109, 125, 1);
    background-color: #fff;
  }

  &::before {
    position: absolute;
    top: 0;
    width: 100%;
    height: 1px;
    content: ' ';
    transform: scaleY(0.5);
    background-color: rgba(0, 0, 0, 0.1);

    .theme-white& {
      height: 0;
      content: none;
    }
  }

  .keyword-bar {
    display: flex;
    align-items: center;
    padding: 0.2rem 0 0.2rem 0.32rem;

    .title {
      flex-shrink: 0;
      color: rgba(255, 255, 255, 0.9);
      font-size: 0.2rem;

      .theme-white& {
        color: #676d7d;
        font-weight: 500;
      }
    }

    .keyword-sroll {
      flex: 1;
      align-self: stretch;
      display: flex;
      align-items: center;
      margin-left: 0.24rem;
      overflow-x: scroll;
      color: rgba(255, 255, 255, 0.9);

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .capsule {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      padding: 0.08rem 0.24rem;
      margin-right: 0.24rem;
      box-sizing: border-box;
      border-radius: 0.54rem;
      background: rgba(34, 34, 34, 0.5);
      border: 0.01rem dashed rgba(255, 255, 255, 0.08);
      font-weight: 500;
      font-size: 0.2rem;
      line-height: 0.32rem;

      .theme-white& {
        background-color: #fff;
        color: rgba(103, 109, 125, 1);
        border: 0.5px solid rgba(74, 80, 92, 0.16);
      }
    }
  }

  .input-bar {
    position: relative;
    display: flex;
    align-items: center;
    padding: 0 0.32rem;

    &.inputing {
      padding-right: 0.34rem;
    }

    textarea {
      flex: 1;
      display: block;
      padding: 0.14rem 0 0.14rem 0.24rem;
      border-radius: 0.32rem;
      border: none;
      overflow-x: hidden;
      overflow-y: hidden;
      outline: none;
      resize: none;
      word-break: break-all;
      line-height: 0.36rem;
      font-size: 0.28rem;
      font-family: @font-family-default;
      color: rgba(255, 255, 255, 0.9);
      background-color: rgba(255, 255, 255, 0.08);

      .theme-white& {
        color: rgba(74, 80, 92, 1);
        background-color: rgba(242, 244, 247, 1);
      }

      .theme-ytPink& {
        caret-color: rgba(255, 121, 181, 1); // 光标颜色
      }

      &::placeholder {
        color: rgba(255, 255, 255, 0.4);
        font-size: 0.24rem;
        line-height: 150%;
        font-family: @font-family-default;

        .theme-white& {
          color: #c4c9d5;
        }
      }
    }

    .hint-list {
      position: absolute;
      left: 0;
      width: 100%;

      .hint-item {
        display: flex;
        align-items: center;
        padding-left: 0.34rem;
        height: 0.82rem;
        box-sizing: border-box;
        background-color: #fff;
        color: rgba(25, 25, 25, 1);
        font-size: 0.3rem;

        .theme-white& {
          background-color: #fff;
          color: rgba(136, 142, 158, 1);
        }

        & .keyword-target {
          .theme-ytPink& {
            color: rgba(255, 121, 181, 1);
          }
        }

        &:last-child {
          border-bottom: none;
        }

        p {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .hight-light {
          color: #f7365d;

          .theme-ytPink& {
            color: #ff79b5;
          }
        }
      }
    }

    .send-btn {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-left: 0.24rem;
      width: 1.2rem;
      height: 0.64rem;
      border-radius: 2rem;
      background-color: #f7365d;
      font-size: 0.24rem;
      font-family: @font-family-medium;
      color: rgba(255, 255, 255, 1);
      border: none;

      .theme-ytPink& {
        width: 1.2rem;
        border-radius: 1.9rem;
        background-color: rgba(255, 121, 181, 1);
      }
    }

    .add-icon {
      margin-left: 0.25rem;
      width: 0.68rem;
      height: 0.68rem;
      // background-color: rgba(255, 255, 255, 0.08);
      background-color: #141414;
    }
  }

  .ios-bar {
    height: 0.68rem;
    background-color: #141414;
  }

  .picker-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-right: 0.24rem;

    .img-box {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 0.64rem;
      height: 0.64rem;
      border-radius: 0.16rem;
      background-color: rgba(255, 255, 255, 0.08);
    }

    img {
      display: block;
      width: 0.64rem;
      height: 0.64rem;
    }

    span {
      margin-top: 0.2rem;
      color: rgba(255, 255, 255, 0.9);
      font-size: 0.24rem;
    }

    .file-input {
      position: absolute;
      top: 0;
      left: -10000px;
    }
  }

  .picker-view {
    display: flex;
    // height: 2.21rem;
    height: 3.22rem;
    background-color: #141414;
    border-top: 0.01rem solid rgba(255, 255, 255, 0.06);
    padding: 0.32rem;

    .picker-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-right: 0.64rem;

      // margin-top: 0.4rem;
      // margin-left: 0.74rem;
      // &:first-child {
      //   margin-left: 0.64rem;
      // }
      .img-box {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 1.12rem;
        height: 1.12rem;
        border-radius: 0.16rem;
        background-color: rgba(255, 255, 255, 0.08);
      }

      img {
        display: block;
        // width: 0.7rem;
        // height: 0.7rem;
        width: 0.56rem;
        height: 0.56rem;
      }

      span {
        margin-top: 0.2rem;
        color: rgba(255, 255, 255, 0.9);
        font-size: 0.24rem;
      }

      .file-input {
        position: absolute;
        top: 0;
        left: -10000px;
      }
    }
  }
}
</style>
